import { lazy } from "react";

import type { ChildRoute, RouteConfig } from "@routes/routes.types";
import {
  reputationBybrand,
  reputationBymarket,
  reputationBysegment,
  brandVisibilityBybrand,
  brandVisibilityBysegment,
  brandVisibilityBymarket,
  advertisingBybrand,
  advertisingBymarket,
  advertisingBysegment,
  conversionBybrand,
  conversionBymarket,
  conversionBysegment,
  emotionalBybrand,
  emotionalBymarket,
  emotionalBysegment,
  equityBybrand,
  equityBymarket,
  equityBysegment,
  exclusiveQuestionsBybrand,
  exclusiveQuestionsBysegment,
  reports,
  aiVisibilityBybrand,
  aiVisibilityBymodel,
} from "./routes.children.constant";
import {
  URL_BY_BRAND,
  URL_BY_MODEL,
} from "@/constant/constantsThrougoutProject";

const Equity = lazy(() => import("@pages/Equity/Equity"));
const Conversion = lazy(() => import("@pages/Conversion/Conversion"));
const Emotional = lazy(() => import("@pages/Emotional/Emotional"));
const Advertising = lazy(() => import("@pages/Advertising/Advertising"));
const BrandVisibility = lazy(
  () => import("@/pages/BrandVisibility/BrandVisibility"),
);
const Reputation = lazy(() => import("@/pages/Reputation/Reputation"));

const ExclusiveQuestions = lazy(
  () => import("@pages/ExclusiveQuestions/ExclusiveQuestions"),
);
const AiVisibility = lazy(() => import("@pages/AiVisibility/AiVisibility"));
const Reports = lazy(() => import("@pages/Reports/Reports"));
const Report = lazy(() => import("@pages/Report/Report"));

const getRoutes = (hiddenRoutes?: string[]): RouteConfig[] => {
  const allRoutes: RouteConfig[] = [
    {
      id: "visibility",
      key: "visibility",
      path: "/visibility/:subRoute",
      component: BrandVisibility,
      exact: true,
      name: "Brand Visibility",
      iconKey: "VISIBILITY",
      private: true,
      isAdminRoute: false,
      children: [
        brandVisibilityBysegment,
        brandVisibilityBybrand,
        brandVisibilityBymarket,
      ],
    },
    {
      id: "equity",
      key: "equity",
      path: "/equity/:subRoute",
      component: Equity,
      exact: true,
      name: "Equity",
      iconKey: "EQUITY",
      private: true,
      isAdminRoute: false,
      children: [equityBysegment, equityBybrand, equityBymarket],
    },
    {
      id: "conversion",
      key: "conversion",
      path: "/conversion/:subRoute",
      component: Conversion,
      exact: true,
      iconKey: "CONVERSION",
      name: "Conversion",
      private: true,
      isAdminRoute: false,
      children: [conversionBysegment, conversionBybrand, conversionBymarket],
    },
    {
      id: "emotional",
      key: "emotional",
      path: "/emotional/:subRoute",
      component: Emotional,
      exact: true,
      iconKey: "EMOTIONAL",
      name: "Emotional",
      private: true,
      isAdminRoute: false,
      children: [emotionalBysegment, emotionalBybrand, emotionalBymarket],
    },
    {
      id: "advertising",
      key: "advertising",
      path: "/advertising/:subRoute",
      component: Advertising,
      exact: true,
      name: "Advertising",
      iconKey: "ADVERTISING",
      private: true,
      isAdminRoute: false,
      children: [advertisingBysegment, advertisingBybrand, advertisingBymarket],
    },

    {
      id: "aivisibility",
      key: "aivisibility",
      path: "/aivisibility/:subRoute",
      component: AiVisibility,
      exact: true,
      name: "AI Visibility",
      iconKey: "AI_VISIBILITY",
      private: true,
      isAdminRoute: false,
      children: [aiVisibilityBybrand, aiVisibilityBymodel],
    },
    {
      id: "reputation",
      key: "reputationQrq",
      path: "/reputation/:subRoute",
      component: Reputation,
      exact: true,
      name: "Reputation",
      iconKey: "REPUTATION",
      private: true,
      isAdminRoute: false,
      children: [reputationBysegment, reputationBybrand, reputationBymarket],
    },
    {
      id: "exclusivequestions",
      key: "exclusivequestions",
      path: "/exclusivequestions/:subRoute",
      component: ExclusiveQuestions,
      exact: true,
      name: "Exclusive Q's",
      iconKey: "EXCLUSIVE_QUESTIONS",
      private: true,
      isAdminRoute: false,
      children: [exclusiveQuestionsBysegment, exclusiveQuestionsBybrand],
      recentViewsModuleName: "questions",
    },
    {
      id: "reports",
      key: "reports",
      path: "/reports",
      component: Reports,
      redirectTo: "/reports",
      exact: true,
      name: "Report Builder",
      iconKey: "REPORTS",
      private: true,
      isAdminRoute: false,
      children: [reports],
    },
    {
      id: "reports",
      key: "reports",
      path: "/reports/:subRoute",
      component: Report,
      redirectTo: "/reports",
      exact: true,
      name: "Report",
      iconKey: "REPORTS",
      private: true,
      isAdminRoute: false,
      children: [reports],
      hideNavIcon: true,
    },
  ];

  if (!hiddenRoutes) {
    return allRoutes;
  }

  return allRoutes.reduce((filteredRoutes, route) => {
    if (!hiddenRoutes.includes(route.id as string)) {
      const newRoute = { ...route };
      if (newRoute.children) {
        newRoute.children = newRoute.children.filter(
          (child) => !hiddenRoutes.includes(child.id as string),
        );
      }
      filteredRoutes.push(newRoute);
    }
    return filteredRoutes;
  }, [] as RouteConfig[]);
};

export const ROUTES_CHILDREN_LOOKUP: Record<string, ChildRoute> = {
  "/equity/segment": equityBysegment,
  "/equity/brand": equityBybrand,
  "/equity/market": equityBymarket,
  "/conversion/segment": conversionBysegment,
  "/conversion/brand": conversionBybrand,
  "/conversion/market": conversionBymarket,
  "/emotional/segment": emotionalBysegment,
  "/emotional/brand": emotionalBybrand,
  "/emotional/market": emotionalBymarket,
  "/advertising/segment": advertisingBysegment,
  "/advertising/brand": advertisingBybrand,
  "/advertising/market": advertisingBymarket,
  "/visibility/segment": brandVisibilityBysegment,
  "/visibility/brand": brandVisibilityBybrand,
  "/visibility/market": brandVisibilityBymarket,
  "/reputation/segment": reputationBysegment,
  "/reputation/brand": reputationBybrand,
  "/reputation/market": reputationBymarket,
  "/exclusivequestions/segment": exclusiveQuestionsBysegment,
  "/exclusivequestions/brand": exclusiveQuestionsBybrand,
  [`/aivisibility/${URL_BY_BRAND}`]: aiVisibilityBybrand,
  [`/aivisibility/${URL_BY_MODEL}`]: aiVisibilityBymodel,
};

export default getRoutes;
